import {
  cx,
  LIST_SELECT_STATUS,
  LIST_STATUS,
} from "@/components/shared/checklistClasses";
import { eventNames } from "@/modules/Constants";
import Checklist, {
  type CheckedChange,
  type ChecklistData,
  type ChecklistGroup,
  type ChecklistItem,
} from "@/components/shared/Checklist";
import type { ExperienceId } from "@/types/experience";
import type { ExperienceState } from "@/types/state";

const REGION = "Decorated Checklist";

type ClusterChecklistProps = {
  experienceState: ExperienceState;
  clusterName?: string;
  hasChecks?: boolean;
  onApplicationEvent?: (event: Record<string, unknown>) => void;
};

interface ClusterRecord {
  title?: string;
  subtitle?: string;
  url?: string;
  ids: ExperienceId[];
}

interface ClusterListData extends ChecklistData {
  total: number;
  totalChecked: number;
}

function getListData(
  experienceState: ExperienceState,
  clusterName: string | undefined,
): ClusterListData {
  const experienceData = experienceState.experienceData;
  const favoritesData = experienceState.favoritesData;
  const favorites = new Set(favoritesData.getFavorites());
  const ret: ClusterListData = {
    groups: [],
    total: 0,
    totalChecked: 0,
  };
  const clusters = experienceData.getClusterList(
    clusterName,
  ) as ClusterRecord[];

  for (let n = 0, maxn = clusters.length; n < maxn; n++) {
    const cluster = clusters[n];
    const clusterIds = cluster.ids;
    const items: ChecklistItem[] = [];
    const map: Record<string, ChecklistItem> = {};

    const group: ChecklistGroup & { url?: string; map: typeof map } = {
      title: cluster.title,
      subtitle: cluster.subtitle,
      url: cluster.url,
      items: items,
      map: map,
    };

    let numChecked = 0;

    for (let m = 0, maxm = clusterIds.length; m < maxm; m++) {
      const article = experienceData.getArticleById(clusterIds[m]);

      // Skip articles that don't exist
      if (!article) {
        console.warn(
          `ClusterChecklist: Article with ID ${clusterIds[m]} not found`,
        );
        continue;
      }

      const checked = favorites.has(article.id);

      if (checked) {
        numChecked++;
      }

      const item = {
        id: article.id,
        title: article.title as string,
        checked: checked,
      };

      items.push(item);
      map[article.id] = item;
    }

    ret.groups.push(group);
    ret.total += items.length;
    ret.totalChecked += numChecked;
  }

  return ret;
}

export default function ClusterChecklist({
  experienceState,
  clusterName,
  hasChecks,
  onApplicationEvent,
}: ClusterChecklistProps) {
  const onCheckedChanged = (items: CheckedChange[]) => {
    if (onApplicationEvent) {
      onApplicationEvent({
        name: eventNames.UPDATE_FAVORITES,
        region: REGION,
        items: items,
      });
    }
  };

  const experienceData = experienceState.experienceData;
  const listData = getListData(experienceState, clusterName);
  const titleText = experienceData.title
    ? experienceData.title
    : experienceData.name;
  const title = titleText ? (
    <h1 className="fn favorite-title list-title header-1 tw:mt-[15px]">
      {titleText}
    </h1>
  ) : null;

  const listStatus = hasChecks ? (
    <div className={cx("list-status", LIST_STATUS)}>
      <div className={LIST_SELECT_STATUS}>
        {listData.totalChecked} of {listData.total} selected
      </div>
    </div>
  ) : null;

  return (
    <div className="hrecipe tw:grow list-scroller tw:overflow-auto tw:[padding:0_16px_6px_16px]">
      {title}
      {listStatus}
      <Checklist
        listData={listData}
        hasChecks={hasChecks}
        onCheckedChanged={onCheckedChanged}
      />
    </div>
  );
}
